/**
 * Connectivity monitor — single source of truth for "is the backend reachable?"
 *
 * Uses both `navigator.onLine` AND a lightweight HEAD heartbeat on /api/health
 * because `navigator.onLine` only catches the physical layer (cable / wifi)
 * and not "wifi up but VPN down" or "captive portal".
 *
 * Subscribe via `subscribeConnectivity(cb)`. Cb receives `{ online: bool, lastOk: ts }`.
 */
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEARTBEAT_INTERVAL_ONLINE = 30_000;   // poll every 30s when online
const HEARTBEAT_INTERVAL_OFFLINE = 5_000;   // probe every 5s when offline
const HEARTBEAT_TIMEOUT = 8_000;            // generous: corporate proxies can be slow
const CONSECUTIVE_FAILURES_TO_GO_OFFLINE = 2; // debounce single-shot blips

const state = {
    online: navigator.onLine !== false,
    lastOk: navigator.onLine === false ? 0 : Date.now(),
    listeners: new Set(),
    timer: null,
    consecutiveFailures: 0,
};


function notify() {
    state.listeners.forEach((cb) => {
        try { cb({ online: state.online, lastOk: state.lastOk }); } catch (_e) { /* swallow */ }
    });
}

function setOnline(value) {
    if (state.online === value) return;
    state.online = value;
    if (value) {
        state.lastOk = Date.now();
        state.consecutiveFailures = 0;
    }
    notify();
    schedule();
}


async function probe() {
    // If the browser itself says we're offline (no wifi / no cable), trust it.
    if (navigator.onLine === false) {
        state.consecutiveFailures = CONSECUTIVE_FAILURES_TO_GO_OFFLINE;
        setOnline(false);
        return false;
    }
    try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), HEARTBEAT_TIMEOUT);
        const res = await fetch(`${API_BASE}/health`, {
            method: "GET",
            cache: "no-store",
            signal: ctrl.signal,
        });
        clearTimeout(timer);
        if (res.ok) {
            state.consecutiveFailures = 0;
            setOnline(true);
            return true;
        }
        // Non-2xx is treated as a soft failure (e.g. captive portal, 502)
        state.consecutiveFailures += 1;
    } catch (_e) {
        state.consecutiveFailures += 1;
    }
    // Only flip to offline after N consecutive failures to ignore transient blips
    if (state.consecutiveFailures >= CONSECUTIVE_FAILURES_TO_GO_OFFLINE) {
        setOnline(false);
    } else {
        // Reschedule a quicker re-probe to recover fast from transient blip
        schedule();
    }
    return false;
}

function schedule() {
    if (state.timer) clearTimeout(state.timer);
    const delay = state.online ? HEARTBEAT_INTERVAL_ONLINE : HEARTBEAT_INTERVAL_OFFLINE;
    state.timer = setTimeout(probe, delay);
}


export function isOnline() {
    return state.online;
}

export function lastOkAt() {
    return state.lastOk;
}

export function subscribeConnectivity(cb) {
    state.listeners.add(cb);
    cb({ online: state.online, lastOk: state.lastOk });
    return () => state.listeners.delete(cb);
}

/** Force an immediate probe (useful after a failed fetch). */
export function pingNow() {
    return probe();
}

// Initialise: hook into the browser events + start polling
if (typeof window !== "undefined") {
    window.addEventListener("online", () => { setOnline(true); probe(); });
    window.addEventListener("offline", () => setOnline(false));
    // Kick off the first probe shortly after page load
    setTimeout(probe, 1500);
}
